import { LockOutlined, SearchOutlined } from "@ant-design/icons";
import { Input, Table, Tag } from "antd";
import { ColumnsType, FilterValue } from "antd/lib/table/interface";
import { JsonReportDetail } from "api/generated";
import { useMemo, useState } from "react";

export interface TransferItem extends JsonReportDetail {
  key: string;
  disabled: boolean;
  hasError: boolean;
  hasReport: boolean;
}

interface ReportSelectorPanelProps {
  direction: "left" | "right";
  items: TransferItem[];
  search: string;
  onSearchChange: (v: string) => void;
  selectedKeys: string[];
  onItemSelect: (key: string, selected: boolean) => void;
  onItemSelectAll: (keys: string[], selected: boolean) => void;
  /** Tamaño de pagina en mobil; en Desktop se usa scroll */
  pageSize?: number;
  scrollY?: number;
}

export function getDistributionType(
  senerLicense?: string,
): "Planta" | "Estacion" {
  return senerLicense?.includes("DIST/PLA") ? "Planta" : "Estacion";
}

function ReportSelectorPanel({
  direction,
  items,
  search,
  onSearchChange,
  selectedKeys,
  onItemSelect,
  onItemSelectAll,
  pageSize,
  scrollY,
}: ReportSelectorPanelProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const [activeFilters, setActiveFilters] = useState<
    Record<string, FilterValue | null>
  >({});

  const itemsFilterByText: TransferItem[] = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (item) =>
        item.distributionPointName?.toLowerCase().includes(q) ||
        item.senerLicense?.toLowerCase().includes(q),
    );
  }, [items, search]);

  const visibleItems: TransferItem[] = useMemo(() => {
    const typeFilter = activeFilters["senerLicense"] as string[] | null;
    if (!typeFilter || typeFilter.length === 0) return itemsFilterByText;
    return itemsFilterByText.filter((item) =>
      typeFilter.includes(getDistributionType(item.senerLicense)),
    );
  }, [itemsFilterByText, activeFilters]);

  // Posibles item keys seleccionables (para descargar)
  const eligibleKeys = useMemo(
    () => visibleItems.filter((r) => !r.disabled).map((r) => r.key),
    [visibleItems],
  );

  const allEligibleSelected =
    eligibleKeys.length > 0 &&
    eligibleKeys.every((k) => selectedKeys.includes(k));
  const someEligibleSelected = eligibleKeys.some((k) =>
    selectedKeys.includes(k),
  );

  function buildColumns(
    direction: "left" | "right",
  ): ColumnsType<TransferItem> {
    const isLeftPanel = direction === "left";
    const columns: ColumnsType<TransferItem> = [
      {
        title: "Punto de distribución",
        dataIndex: "distributionPointName",
        ellipsis: true,
        width: isLeftPanel ? "20%" : "40%",
      },
      {
        title: "No. Permiso",
        dataIndex: "senerLicense",
        ellipsis: true,
        width: isLeftPanel ? "20%" : "40%",
        render: (v?: string) => v ?? "—",
      },
      {
        title: "Tipo",
        dataIndex: "senerLicense",
        width: isLeftPanel ? "10%" : "20%",
        filters: [
          { text: "Planta", value: "Planta" },
          { text: "Estacion", value: "Estacion" },
        ],
        onFilter: (value, record) =>
          getDistributionType(record.senerLicense) === value,
        render: (v?: string) => {
          const tipo = getDistributionType(v);
          return <Tag color={tipo === "Planta" ? "blue" : "green"}>{tipo}</Tag>;
        },
      },
    ];

    if (isLeftPanel) {
      columns.push({
        title: "Detalles",
        width: "50%",
        render: (_: unknown, record: TransferItem) => {
          if (!record.hasReport)
            return (
              <span style={{ color: "#cf1322", fontSize: 12 }}>
                <LockOutlined style={{ marginRight: 4 }} />
                Bloqueado: no existe JSON para este reporte
              </span>
            );
          if (record.hasError)
            return (
              <span style={{ color: "#cf1322", fontSize: 12 }}>
                <LockOutlined style={{ marginRight: 4 }} />
                Bloqueado por errores en el reporte
              </span>
            );
          return null;
        },
      });
    }

    return columns;
  }

  const columns = buildColumns(direction);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Input
        placeholder="Buscar por nombre o permiso..."
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => {
          onSearchChange(e.target.value);
          setCurrentPage(1);
        }}
        style={{ marginBottom: 8 }}
        allowClear
      />
      <Table<TransferItem>
        rowSelection={{
          getCheckboxProps: (item) => ({ disabled: item.disabled }),
          onSelect: ({ key }, selected) => onItemSelect(key, selected),
          selectedRowKeys: selectedKeys,
          columnTitle: (
            <input
              type="checkbox"
              checked={allEligibleSelected}
              ref={(el) => {
                if (el)
                  el.indeterminate =
                    someEligibleSelected && !allEligibleSelected;
              }}
              onChange={(e) => onItemSelectAll(eligibleKeys, e.target.checked)}
              title="Seleccionar todos los filtrados"
              style={{ cursor: "pointer", width: 14, height: 14 }}
            />
          ),
        }}
        columns={columns}
        dataSource={visibleItems}
        onChange={(_pagination, filters) => {
          setActiveFilters(filters);
          setCurrentPage(1);
        }}
        size="small"
        rowKey="key"
        sticky={{ offsetHeader: 0 }}
        scroll={{ x: "max-content", y: scrollY }}
        // Desktop: Sin Paginacion, Screll
        // Mobile: Items Paginados
        pagination={
          pageSize
            ? {
                current: currentPage,
                pageSize,
                size: "small",
                showSizeChanger: false,
                showTotal: (total) => `${total} registros`,
                onChange: setCurrentPage,
                style: {
                  position: "sticky",
                  bottom: 0,
                  background: "#fff",
                  padding: "6px 8px",
                  zIndex: 2,
                  margin: 0,
                  borderTop: "1px solid #f0f0f0",
                },
              }
            : false
        }
        onRow={({ key, disabled }) => ({
          onClick: () => {
            if (!disabled) onItemSelect(key, !selectedKeys.includes(key));
          },
          style: { cursor: disabled ? "not-allowed" : "pointer" },
        })}
      />
    </div>
  );
}

export default ReportSelectorPanel;
